import React, { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useCurrency, useI18n, useLogger } from '~Hooks';
import { StageLayer } from '../../styles';
import Header from './Header';
import { useExpressCartSummary } from '../../Providers/ExpressCartProvider';
import { useExpressProductLibrary } from '../../Providers/ExpressProductLibraryProvider';
import CategoryRail from './CategoryRail';
import MenuSection from './MenuSection';
import CartBar from './CartBar';
import CartDrawer from './CartDrawer';
import ItemDetail from './ItemDetail';
import CartPanel from './CartPanel';
import useIsLandscape from '../../Hooks/useIsLandscape';
import { CartLineEdit, EposMenuItem } from '../../types';

/** How long the sheet takes to leave when checkout starts. EposFlow's CSSTransition uses it too. */
export const MENU_EXIT_MS = 380;

/** The fly-in on mount - a touch slower than the exit, which should feel snappy. */
const MENU_ENTER_MS = 550;

/** Portrait zooms in instead of sliding (see Sheet) and can afford to be quicker. */
const MENU_ENTER_PORTRAIT_MS = 380;
const MENU_EXIT_PORTRAIT_MS = 260;

/**
 * Mounted for the whole EPOS flow, sitting over the welcome background but
 * hidden and non-interactive until the shopper picks a service type - that
 * way the first tap only has to play the slide-in transition instead of
 * also mounting and rendering the whole menu sheet for the first time.
 * Only the sheet below flies up - this layer itself never moves, so the
 * header stays put on its own spot. The `epos-menu-*` classes come from
 * EposFlow's CSSTransition on this layer.
 */
const Layer = styled(StageLayer)`
  align-items: center;
  pointer-events: none;
  /* Parked below the screen the sheet is still painted - its upward shadow
     (and a zoom-rounded sliver of its edge) shows through on the welcome
     screen. Hidden outright while down; visibility keeps the tree mounted
     and laid out, so the first tap still only plays the slide. */
  visibility: hidden;

  &.epos-menu-enter,
  &.epos-menu-enter-active,
  &.epos-menu-enter-done,
  &.epos-menu-exit,
  &.epos-menu-exit-active {
    visibility: visible;
  }

  &.epos-menu-enter-active,
  &.epos-menu-enter-done {
    pointer-events: auto;
  }
`;

/**
 * The free strip at the top - the background stays visible behind it. The
 * header fades in in place rather than flying, since it never leaves the top
 * of the screen.
 */
const HeaderSlot = styled.div`
  width: 96%;
  height: 12%;
  flex: 0 0 12%;
  opacity: 0;
  transition: opacity 0.4s ease-out;

  .epos-menu-enter-active &,
  .epos-menu-enter-done & {
    opacity: 1;
  }

  /* Checkout starting: fade back out, a touch faster than the fade in. */
  .epos-menu-exit-active & {
    transition-duration: 0.2s;
    opacity: 0;
  }
`;

/**
 * The menu card itself. 96% wide, running to the bottom edge of the screen,
 * flying up from below when the shopper starts. A flex row: the sliding
 * menu/detail area, and on landscape screens the docked CartPanel to its right.
 */
const Sheet = styled.div`
  width: 96%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  border-radius: 32px 32px 0 0;
  background: var(--eposSurface);
  box-shadow: 0 -24px 60px rgba(0, 0, 0, 0.45);
  font-family: var(--eposFontFamily);
  transform: translate3d(0, 100%, 0);
  transition: transform ${MENU_ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  .epos-menu-enter-active &,
  .epos-menu-enter-done & {
    transform: translate3d(0, 0, 0);
  }

  /* Checkout starting: slide back down behind the bottom edge, same curve but snappier. */
  .epos-menu-exit-active & {
    transition-duration: ${MENU_EXIT_MS}ms;
    transform: translate3d(0, 100%, 0);
  }

  /* Portrait: the full-height slide drops frames on the ELO hardware, so the
     sheet zooms in from slightly small and transparent instead - a short
     scale over a much smaller travel. Reversed on exit. */
  @media (orientation: portrait) {
    transform-origin: 50% 60%;
    transform: scale(0.92);
    opacity: 0;
    transition: transform ${MENU_ENTER_PORTRAIT_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity ${MENU_ENTER_PORTRAIT_MS}ms ease-out;
    will-change: transform, opacity;

    .epos-menu-enter-active &,
    .epos-menu-enter-done & {
      transform: scale(1);
      opacity: 1;
    }

    .epos-menu-exit-active & {
      transition-duration: ${MENU_EXIT_PORTRAIT_MS}ms;
      transform: scale(0.92);
      opacity: 0;
    }
  }
`;

/** How long the pane slide takes - the detail pane unmounts once it has slid out. */
const SLIDE_DURATION_MS = 500;

/**
 * Clips the sliding track. Its own box, so the track's 200% is measured
 * against the menu area only and a docked cart beside it is left alone.
 */
const SheetMain = styled.div`
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  position: relative;
  display: flex;
  overflow: hidden;
`;

/**
 * Two panes side by side, twice the sheet's width. Sliding to the detail pane
 * is a transform toggled by the `is-detail-open` class, set imperatively from a
 * ref - the menu underneath never re-renders for it.
 */
const Track = styled.div`
  /* SheetMain is a flex row - without flex-shrink: 0 the track would be
     squeezed to its width and both panes would show at half size. */
  flex: 0 0 200%;
  width: 200%;
  height: 100%;
  display: flex;
  flex-direction: row;
  transition: transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  &.is-detail-open {
    transform: translate3d(-50%, 0, 0);
  }

  /* Set for one frame when a new session resets the track, so it jumps instead of sliding. */
  &.is-resetting {
    transition: none;
  }
`;

const MenuPane = styled.div`
  flex: 0 0 50%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
`;

const DetailPane = styled.div`
  flex: 0 0 50%;
  min-width: 0;
  height: 100%;
`;

const Scroller = styled.div`
  /* Positioned so each section's offsetTop is measured from here - the
     rail tap scrolls to that number directly. */
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  /* No top padding - each section brings its own, so the first one sits flush at scrollTop 0. */
  /* Lets the last category reach the scroll-spy band at the top of the pane. */
  padding-bottom: 75vh;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Message = styled.div`
  margin: auto;
  text-align: center;
  padding: 48px;
  font-size: 24px;
  color: var(--eposMutedText);
`;

interface Props {
  onStartOver: () => void;
  /** Navigation only: the shopper wants to pay - EposFlow swaps the sheet for the checkout. */
  onCheckout: () => void;
  /** Bumped by EposFlow each time a fresh order starts, so the menu scrolls back to the top. */
  sessionKey: number;
}

/** The ref is the layer node - EposFlow wraps this in a CSSTransition for the exit slide. */
const Menu = forwardRef<HTMLDivElement, Props>(
  ({ onStartOver, onCheckout, sessionKey }, forwardedRef) => {
    const { i18n } = useI18n();
    const logger = useLogger('epos-menu');
    const currency = useCurrency();
    /** Items/totals only - doesn't re-render for a membership lookup or pickup time change. */
    const expressCart = useExpressCartSummary();
    /** Already filtered to menu items - see ExpressProductLibraryProvider. */
    const productLibrary = useExpressProductLibrary();
    /** Landscape docks the cart in the sheet; portrait uses the floating bar and drawer. */
    const isLandscape = useIsLandscape();

    const railRef = useRef<HTMLElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    /** Set while a rail tap is scrolling the pane, so the spy doesn't fight it. */
    const suppressSpyRef = useRef(false);
    /** Debounce for "the smooth scroll has stopped" - see handleSelect. */
    const settleTimerRef = useRef<number | undefined>(undefined);
    /** Removes the scroll listener of the tap in progress, if any. */
    const stopSettleWatchRef = useRef<() => void>(() => undefined);

    const categories = productLibrary.menuCategories;

    const layerRef = useRef<HTMLDivElement>(null);
    /** One node, two refs: ours for class toggles, the parent's for its CSSTransition. */
    const setLayerRef = useCallback(
      (node: HTMLDivElement | null) => {
        (layerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );
    const trackRef = useRef<HTMLDivElement>(null);
    const slideTimerRef = useRef<number | undefined>(undefined);
    /**
     * The item shown in the detail pane, and - when opened via "Ändra"/"Edit"
     * on a cart line rather than from the menu - what to prefill and which
     * cart lines committing should edit in place. Kept mounted until the
     * slide back has finished.
     */
    const [detailPane, setDetailPane] = useState<{
      item: EposMenuItem;
      editing?: CartLineEdit;
      /** Bumped on every open, so reopening the same item mid-slide still mounts a fresh pane. */
      openId: number;
    } | null>(null);
    const openIdRef = useRef(0);

    /** The line being edited. Separate from detailPane so it clears when the slide-back starts, not when it ends. */
    const [editingLine, setEditingLine] = useState<CartLineEdit | undefined>(undefined);
    /** Mirror for editLine, so it stays stable and can tell a second tap on the same badge. */
    const editingLineRef = useRef<CartLineEdit | undefined>(undefined);
    editingLineRef.current = editingLine;

    const cartBarRef = useRef<HTMLDivElement>(null);
    const hasCartBar = expressCart.items.length > 0;
    const cartDrawerRef = useRef<HTMLDivElement>(null);
    /** Whether the drawer is open, without a render - both refs are toggled together. */
    const cartOpenRef = useRef(false);

    const handleOpenCart = useCallback(() => {
      /** A tap landing together with "Continue to payment" must not open the drawer over the checkout. */
      const layerClasses = layerRef.current?.classList;
      if (
        layerClasses?.contains('epos-menu-exit') ||
        layerClasses?.contains('epos-menu-exit-active') ||
        layerClasses?.contains('epos-menu-exit-done')
      ) {
        logger.info('Ignored cart open - the menu is leaving for checkout');
        return;
      }
      logger.info('Cart opened', { itemCount: expressCart.items.length });
      cartOpenRef.current = true;
      cartBarRef.current?.classList.add('is-cart-open');
      cartDrawerRef.current?.classList.add('is-open');
    }, [expressCart.items.length]);

    const handleCloseCart = useCallback(() => {
      logger.info('Cart closed');
      cartOpenRef.current = false;
      cartBarRef.current?.classList.remove('is-cart-open');
      cartDrawerRef.current?.classList.remove('is-open');
    }, []);

    // Removing the last item from inside the drawer closes it as well, and a
    // drawer left open when the screen turns landscape is dropped too.
    useEffect(() => {
      if (cartOpenRef.current && (expressCart.items.length === 0 || isLandscape)) {
        handleCloseCart();
      }
    }, [expressCart.items.length, isLandscape, handleCloseCart]);

    const openPane = useCallback((item: EposMenuItem, editing?: CartLineEdit) => {
      window.clearTimeout(slideTimerRef.current);
      openIdRef.current += 1;
      setDetailPane({ item, editing, openId: openIdRef.current });
      setEditingLine(editing);
      // The detail pane mounts off-screen (it's the other half of the 200%-wide
      // track) so mounting it doesn't itself cause a visible jump - but on slow
      // hardware, mounting all its JSX and starting the slide in the same frame
      // still drops frames. Waiting a paint lets the browser get the new pane
      // on screen (still hidden) before the slide - driven by a class, not
      // React - has to animate anything, so the slide itself stays smooth.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          trackRef.current?.classList.add('is-detail-open');
          layerRef.current?.classList.add('is-detail-open');
        });
      });
    }, []);

    const openDetail = useCallback((item: EposMenuItem) => openPane(item), [openPane]);

    const closeDetail = useCallback(() => {
      setEditingLine(undefined);
      trackRef.current?.classList.remove('is-detail-open');
      layerRef.current?.classList.remove('is-detail-open');
      // Unmount only once the pane is out of view, so it doesn't blank mid-slide.
      window.clearTimeout(slideTimerRef.current);
      slideTimerRef.current = window.setTimeout(() => setDetailPane(null), SLIDE_DURATION_MS);
    }, []);

    useEffect(() => () => window.clearTimeout(slideTimerRef.current), []);

    /** The badge lives inside the drawer itself, so editing closes it first, same as checkout. Tapping the badge of the line already being edited backs out instead. */
    const editLine = useCallback(
      (edit: CartLineEdit) => {
        if (editingLineRef.current?.ids[0] === edit.ids[0]) {
          logger.info('Edit badge tapped again - closing edit', { itemId: edit.item.id });
          closeDetail();
          return;
        }
        handleCloseCart();
        openPane(edit.item, edit);
      },
      [handleCloseCart, openPane, closeDetail],
    );

    /** Drops the drawer first so both slide down together, then hands over to the checkout. */
    const handleCheckout = useCallback(() => {
      logger.info('Checkout started from menu', { itemCount: expressCart.items.length });
      handleCloseCart();
      onCheckout();
    }, [expressCart.items.length, handleCloseCart, onCheckout]);

    /** Moves the `is-active` class between rail buttons - no render, no scroll lag. */
    const markActive = useCallback((categoryId: string) => {
      const rail = railRef.current;
      if (!rail) {
        return;
      }

      const next = rail.querySelector(`[data-category-id="${categoryId}"]`);
      // Checked against the DOM rather than the ref: a button that re-rendered
      // may have lost the class while the ref still claims it is marked.
      if (!next || next.classList.contains('is-active')) {
        return;
      }

      rail
        .querySelectorAll('[data-category-id].is-active')
        .forEach((node) => node.classList.remove('is-active'));

      next.classList.add('is-active');

      // Scrolled by arithmetic rather than scrollIntoView, which would also walk
      // up and scroll the sliding track behind us. Centered, smooth, clamped at 0.
      if (next instanceof HTMLElement) {
        const delta = next.getBoundingClientRect().top - rail.getBoundingClientRect().top;
        const target = rail.scrollTop + delta - rail.clientHeight / 2 + next.clientHeight / 2;
        rail.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      }
    }, []);

    const handleSelect = useCallback(
      (categoryId: string) => {
        logger.info('Category selected from rail', { categoryId });

        const scroller = scrollerRef.current;
        const section = scroller?.querySelector(`[data-category-id="${categoryId}"]`);
        if (!scroller || !(section instanceof HTMLElement)) {
          markActive(categoryId);
          return;
        }

        // The smooth scroll passes every section in between. The spy stays
        // paused until the scroller has actually stopped moving - detected by
        // the scroll events going quiet - rather than for a guessed duration,
        // so the rail never jumps to a category we only passed through.
        stopSettleWatchRef.current();
        suppressSpyRef.current = true;

        const settle = () => {
          stopSettleWatchRef.current();
          suppressSpyRef.current = false;
          markActive(categoryId);
        };
        const onScroll = () => {
          window.clearTimeout(settleTimerRef.current);
          settleTimerRef.current = window.setTimeout(settle, 120);
        };
        scroller.addEventListener('scroll', onScroll, { passive: true });
        stopSettleWatchRef.current = () => {
          scroller.removeEventListener('scroll', onScroll);
          window.clearTimeout(settleTimerRef.current);
          stopSettleWatchRef.current = () => undefined;
        };

        markActive(categoryId);

        // offsetTop is relative to the (positioned) scroller, so this is the
        // resting position of the section's top edge - independent of any
        // smooth scroll still in flight from the previous tap.
        const target = Math.max(0, section.offsetTop);
        if (Math.abs(scroller.scrollTop - target) < 1) {
          // Nothing to scroll, so no scroll events would ever settle us.
          settle();
          return;
        }
        // Kick off in case a scroll event never arrives (e.g. content shorter than the pane).
        onScroll();
        scroller.scrollTo({ top: target, behavior: 'smooth' });
      },
      [markActive],
    );

    /**
     * Scroll-spy. An IntersectionObserver with a top-biased root margin means the
     * browser tells us which section is at the top of the pane - we never listen
     * to scroll events or store scroll position in state.
     */
    useEffect(() => {
      const scroller = scrollerRef.current;
      if (!scroller || !categories.length) {
        return undefined;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (suppressSpyRef.current) {
            return;
          }

          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

          const categoryId = visible?.target.getAttribute('data-category-id');
          if (categoryId) {
            markActive(categoryId);
          }
        },
        { root: scroller, rootMargin: '0px 0px -75% 0px', threshold: 0 },
      );

      scroller.querySelectorAll('[data-category-id]').forEach((node) => observer.observe(node));
      markActive(categories[0].id);

      return () => observer.disconnect();
    }, [categories, markActive]);

    // A fresh order starting (start over, or after a completed purchase) should
    // land back at the top of the menu rather than wherever the last shopper
    // scrolled to - the menu itself stays mounted the whole time, so nothing
    // else would reset this. Skipped on the very first render, since both
    // panes already start at scrollTop 0 then.
    const isFirstRenderRef = useRef(true);
    useEffect(() => {
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        return;
      }
      // `behavior: 'instant'` is the only way to jump: the scroller's own
      // `scroll-behavior: smooth` applies to a plain `scrollTop = 0` too, which
      // would visibly scroll through the whole menu as the sheet flies in.
      scrollerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
      railRef.current?.scrollTo({ top: 0, behavior: 'instant' });
      if (categories[0]) {
        markActive(categories[0].id);
      }
      // A detail pane or open cart drawer left behind by "start over" or the
      // idle timeout must not greet the next shopper. The sheet is off-screen
      // while this runs, so the pane is dropped at once instead of sliding back.
      logger.info('New session - resetting detail pane and cart drawer', { sessionKey });
      window.clearTimeout(slideTimerRef.current);
      const track = trackRef.current;
      if (track) {
        // Jump, don't slide: the transition is switched off, the class is
        // removed, and a forced reflow commits that before it is switched on again.
        track.classList.add('is-resetting');
        track.classList.remove('is-detail-open');
        void track.offsetWidth;
        track.classList.remove('is-resetting');
      }
      layerRef.current?.classList.remove('is-detail-open');
      setDetailPane(null);
      setEditingLine(undefined);
      if (cartOpenRef.current) {
        handleCloseCart();
      }
    }, [sessionKey]);

    useEffect(() => () => stopSettleWatchRef.current(), []);

    const renderSheetContents = () => {
      if (!categories.length) {
        return <Message>{i18n('SSK.NoItems')}</Message>;
      }

      return (
        <>
          <SheetMain>
            <Track ref={trackRef} data-testid="epos-track">
              <MenuPane>
                {/* One category has nothing to navigate to - the menu takes the full
                    width and starts at the top. markActive and the session reset
                    already skip a missing rail. */}
                {categories.length > 1 && (
                  <CategoryRail
                    categories={categories}
                    onSelect={handleSelect}
                    containerRef={railRef}
                  />
                )}
                <Scroller ref={scrollerRef}>
                  {categories.map((category) => (
                    <MenuSection
                      key={category.id}
                      category={category}
                      onOpenDetail={openDetail}
                      outOfStockIds={productLibrary.outOfStockIds}
                    />
                  ))}
                </Scroller>
              </MenuPane>
              <DetailPane>
                {/* Keyed per open so the selection and the commit guard reset every time. */}
                {detailPane && (
                  <ItemDetail
                    key={detailPane.openId}
                    item={detailPane.item}
                    editing={detailPane.editing}
                    onBack={closeDetail}
                  />
                )}
              </DetailPane>
            </Track>
          </SheetMain>
          {/* Outside the track, so it stays put while the detail pane slides in. */}
          {isLandscape && (
            <CartPanel onCheckout={handleCheckout} onEditLine={editLine} editing={editingLine} />
          )}
        </>
      );
    };

    return (
      <Layer ref={setLayerRef}>
        <HeaderSlot>
          <Header onStartOver={onStartOver} />
        </HeaderSlot>
        <Sheet data-testid="epos-menu-sheet">{renderSheetContents()}</Sheet>

        {/* Portrait only - landscape has the cart docked in the sheet. Mounted up
          front, hidden until opened, so the tap that opens it only plays the
          slide instead of also mounting and rendering the cart contents. */}
        {!isLandscape && (
          <CartDrawer
            ref={cartDrawerRef}
            onClose={handleCloseCart}
            onEditLine={editLine}
            editing={editingLine}
          />
        )}

        {/* Mounted on the first added item, so its fly-in plays exactly then.
          Lives on the layer, not the sheet, so it spans the whole screen. */}
        {!isLandscape && hasCartBar && (
          <CartBar
            ref={cartBarRef}
            count={expressCart.items.length}
            total={currency.format(expressCart.total - expressCart.discount)}
            originalTotal={currency.format(expressCart.total)}
            onOpenCart={handleOpenCart}
            onCheckout={handleCheckout}
            disabled={!expressCart.payable}
          />
        )}
      </Layer>
    );
  },
);

Menu.displayName = 'Menu';

export default memo(Menu);
